import React, { useState } from "react";
import { Form, Input, message, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../../shared/api/AllApi";
import { invalidateCatalog } from "../../../hooks/useCatalogData";
import usePermissions from "../../../hooks/usePermissions";
import { FormPage, FormCard, inputStyle, T } from "../../../shared/ui/PageShell";

const AddProducts = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManageImages = hasPermission("products.images");
  const [submitting, setSubmitting] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const handleImageUpload = async ({ file }) => {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 4 * 1024 * 1024) {
      message.error(t("productImageInvalid", "Use a JPG, PNG, or WebP image up to 4 MB."));
      return false;
    }
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post("/products/image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.status === 200) { setImageUrl(res.data.path); }
      else { message.error(t("errorMessage", "Failed to submit form. Please try again.")); setImagePreview(null); }
    } catch { message.error(t("errorMessage", "Failed to submit form. Please try again.")); setImagePreview(null); }
    finally { setImageUploading(false); URL.revokeObjectURL(previewUrl); }
    return false;
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const nameVal = (values.name || "").trim();
      const productData = {
        // Column requires valid JSON; store the single name across all locales.
        name: JSON.stringify({ en: nameVal, ar: nameVal, ku: nameVal }),
      };
      if (imageUrl) productData.image_url = imageUrl;
      await apiClient.post("/products", productData);
      invalidateCatalog("products");
      message.success(t("successMessage", "Form submitted successfully"));
      navigate("/products");
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || t("errorMessage", "Failed to submit form. Please try again."));
    } finally { setSubmitting(false); }
  };

  return (
    <FormPage
      breadcrumb={[t("sidebarSectionCatalog", "Catalog"), t("products", "Products"), t("addItem", "Add Item")]}
      title={t("addProduct", "Add Product")}
      subtitle={t("addProductSubtitle", "Add a new product to the catalog")}
      onBack={() => navigate("/products")}
      onSave={onSave}
      saving={submitting}
      sidePanel={canManageImages ? (
        <FormCard title={t("image", "Image")}>
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={({ file }) => handleImageUpload({ file: file.originFileObj || file })}
              disabled={imageUploading}
            >
              {imagePreview || imageUrl ? (
                <img
                  src={imagePreview || imageUrl}
                  alt={t("productImageAlt", "Product")}
                  style={{ width: 140, height: 140, objectFit: "cover", borderRadius: 12, border: `2px dashed ${T.border}`, cursor: "pointer" }}
                />
              ) : (
                <div style={{ width: 140, height: 140, borderRadius: 12, border: `2px dashed ${T.border}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 8, color: T.muted, margin: "0 auto" }}>
                  <UploadOutlined style={{ fontSize: 28 }} />
                  <span style={{ fontSize: 12 }}>{t("image", "Image")}</span>
                </div>
              )}
            </Upload>
          </div>
        </FormCard>
      ) : null}
    >
      <Form form={form} layout="vertical" requiredMark scrollToFirstError>
        <FormCard title={t("productInfo", "Product Information")}>
          <Form.Item name="name" label={t("name", "Name")} rules={[{ required: true, message: t("enterProductName", "Enter product name") }]}>
            <Input placeholder={t("enterProductName", "Enter product name")} style={inputStyle} />
          </Form.Item>
        </FormCard>
      </Form>
    </FormPage>
  );
};

export default AddProducts;
